import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import * as dayjs from 'dayjs';

import { DispatchService } from '../../providers/dispatch/dispatch.service';
import { LoadingService } from '../../providers/loading/loading.service';
import { NotificationService } from '../../providers/notification/notification.service';
import { UserService } from '../../providers/user/user.service';
import { DirectivesModule } from '../../directives.module';

interface DialogData {
  id?: number;
  orderId: number;
  message: string;
  pickDate: string;
  deliveryDate: string;
}

@Component({
  selector: 'app-dispatch',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    DirectivesModule,
  ],
  templateUrl: './dispatch.component.html',
  styleUrls: ['./dispatch.component.scss'],
})
export class DispatchComponent implements OnInit {
  public message: string;
  public dataForm = this.buildDataForm();
  constructor(
    private fb: FormBuilder,
    private dispatchService: DispatchService,
    private loadingService: LoadingService,
    private userService: UserService,
    private notificationService: NotificationService,
    public dialogRef: MatDialogRef<DispatchComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
  ) { }

  ngOnInit() {
    this.setData();
  }

  private setData() {
    const { deliveryDate, pickDate, orderId, id } = this.data;
    const currentDate = dayjs().toISOString();
    this.dataForm.controls['orderId'].setValue(orderId);
    this.dataForm.controls['pickDate'].setValue(pickDate || currentDate);
    this.dataForm.controls['deliveryDate'].setValue(deliveryDate || currentDate);
    if (id) {
      this.dataForm.controls['pickDate'].disable();
      this.dataForm.controls['deliveryDate'].disable();
    }
  }

  private getValue(key: string) {
    return this.dataForm.get(key).value;
  }

  public checkDispatch() {
    this.loadingService.startLoading();
    if (this.data.id) {
      this.updateDispatch();
    } else {
      this.createDispatch();
    }
  }

  private updateDispatch() {
    this.dispatchService.update(this.data.id, {
      pickDate: this.userService.toIsoDate(this.getValue('pickDate')),
      deliveryDate: this.userService.toIsoDate(this.getValue('deliveryDate')),
    })
      .subscribe(
        (res) => {
          this.dialogRef.close(res);
          this.loadingService.stopLoading();
          this.notificationService.success('Success edited!');
        },
        () => {
          this.loadingService.stopLoading();
          this.dialogRef.close();
        });
  }

  private createDispatch() {
    this.dispatchService.create({
      orderId: this.getValue('orderId'),
      pickDate: this.userService.toIsoDate(this.getValue('pickDate')),
      deliveryDate: this.userService.toIsoDate(this.getValue('deliveryDate')),
    })
      .subscribe(
        (res) => {
          this.dialogRef.close(res);
          this.loadingService.stopLoading();
          this.notificationService.success('Success created!');
        },
        () => {
          this.loadingService.stopLoading();
          this.dialogRef.close();
        });
  }

  private buildDataForm() {
    return this.fb.group({
      orderId: new FormControl<number | null>(null, [Validators.required]),
      pickDate: new FormControl('', [Validators.required]),
      deliveryDate: new FormControl('', [Validators.required]),
    });
  }
}
